<template>
    <div class="scan__overlay" ref="overlayEl">
        <div v-if="showHint && !showPreview" class="scan__hint">
            {{ hintText }}
        </div>
        <div class="reticle" ref="reticleEl" :style="reticleStyle" :class="`reticle--${shape}`">
            <template v-if="!showPreview">
                <div class="reticle__corner reticle__corner_tl" />
                <div class="reticle__corner reticle__corner_tr" />
                <div class="reticle__corner reticle__corner_bl" />
                <div class="reticle__corner reticle__corner_br" />
                <div class="reticle__line" />
            </template>
        </div>

        <svg
            class="scan__mask"
            :viewBox="`0 0 ${overlayWidth} ${overlayHeight}`"
            preserveAspectRatio="none"
            :width="overlayWidth"
            :height="overlayHeight"
        >
            <defs>
                <mask :id="maskId">
                    <rect :width="overlayWidth" :height="overlayHeight" fill="white" />
                    <circle
                        v-if="shape === 'circle'"
                        :cx="hole.x + hole.w / 2"
                        :cy="hole.y + hole.h / 2"
                        :r="hole.w / 2"
                        fill="black"
                    />
                    <rect
                        v-else
                        :x="hole.x"
                        :y="hole.y"
                        :width="hole.w"
                        :height="hole.h"
                        rx="8"
                        ry="8"
                        fill="black"
                    />
                </mask>
            </defs>
            <rect
                :width="overlayWidth"
                :height="overlayHeight"
                fill="rgba(0,0,0,.5)"
                :mask="`url(#${maskId})`"
            />
        </svg>
        <img
            v-if="showPreview && previewImageUrl"
            :src="previewImageUrl"
            alt="Captured image"
            class="scan__preview-img"
        />
    </div>
</template>

<script setup lang="ts">
import type { ScanShape } from '@/types'
import type { StyleValue } from 'vue'
import { ref } from 'vue'

type ScanHole = {
    x: number
    y: number
    w: number
    h: number
}

defineProps<{
    shape: ScanShape
    overlayWidth: number
    overlayHeight: number
    hole: ScanHole
    maskId: string
    reticleStyle: StyleValue
    showHint: boolean
    showPreview: boolean
    previewImageUrl: string | null
    hintText?: string
}>()

const overlayEl = ref<HTMLDivElement | null>(null)
const reticleEl = ref<HTMLDivElement | null>(null)

defineExpose({
    get overlayEl() {
        return overlayEl.value
    },
    get reticleEl() {
        return reticleEl.value
    },
})
</script>

<style scoped lang="stylus">
.scan
    &__overlay
        position absolute
        inset 0
        pointer-events none

    &__hint
        transform translateX(-50%)
        position absolute
        left 50%
        top 25%
        width 100%
        max-width 90vw
        padding 12px 18px
        background url('@/assets/images/scan_hint.png') no-repeat center / cover
        border-radius 10px
        font-size 16px
        line-height 16px
        font-weight 500
        text-align center
        color var(--app-text-light-color)
        pointer-events none
        z-index 40

    &__mask
        position absolute
        inset 0
        display block

    &__preview-img
        position absolute
        left 50%
        top 50%
        z-index 80
        transform translate(-50%, -50%)
        max-width 90vw
        max-height calc(100% - 200px)
        object-fit contain
        border-radius 8px

.reticle
    position absolute
    z-index 80
    top 50%
    left 50%
    transform translate(-50%, -50%)
    border-radius 8px
    --reticle-offset-v 10%
    --reticle-offset-h 4%

    &__corner
        position absolute
        width 34%
        height 45%
        border 3px solid rgba(255,255,255,.95)

        &_tl
            top calc(-1 * var(--reticle-offset-v))
            left calc(-1 * var(--reticle-offset-h))
            border-top-left-radius 20px
            border-right none
            border-bottom none

        &_tr
            top calc(-1 * var(--reticle-offset-v))
            right calc(-1 * var(--reticle-offset-h))
            border-top-right-radius 20px
            border-left none
            border-bottom none

        &_bl
            bottom calc(-1 * var(--reticle-offset-v))
            left calc(-1 * var(--reticle-offset-h))
            border-bottom-left-radius 20px
            border-right none
            border-top none

        &_br
            bottom calc(-1 * var(--reticle-offset-v))
            right calc(-1 * var(--reticle-offset-h))
            border-bottom-right-radius 20px
            border-left none
            border-top none

    &__line
        position absolute
        top 50%
        left 50%
        transform translate(-50%, -50%)
        width 81%
        height 3px
        background rgba(255, 255, 255, 0.95)
        border-radius 1.5px

.reticle--circle
    border-radius 50%
    .reticle__corner
        display none
    &::after
        content ''
        position absolute
        inset -3px
        border-radius 50%
        border 3px solid rgba(255, 255, 255, 0.95)

.reticle--square
    .reticle__corner
        width 28%
        height 28%
</style>
